import React, { useEffect, useMemo, useRef, useState } from "react";
import { useParams, Link } from "react-router-dom";
import ShareButtons from "../../components/ShareButtons";
import BigModal from "../../components/BigModal";
import LeadForm from "../../components/LeadForm/LeadForm";
import { useTranslation } from "react-i18next";
import StickyCTA from "../../components/StickyCTA";
import SEO from "../../components/SEO";
import { getConsent } from "../../analytics/tracker"; 

// Fallback konverzija [text](url) u <a> sa tvojim bojama
const mdLinksToHtml = (text) =>
  text.replace(
    /\[([^\]]+)\]\((https?:\/\/[^\s)]+|\/[^\s)]+)\)/g,
    `<a href="$2" target="_blank" rel="noopener noreferrer" class="text-primary-light dark:text-primary-dark font-medium hover:underline">$1</a>`
  );

const makePostUrl = (lang, slug) => `https://modularobjects.com/${lang}/blog/${slug}`;

const RelatedCard = ({ lang, slugKey }) => {
  return (
    <Link
      to={`/${lang}/blog/${slugKey}`}
      className="block rounded-xl border hover:shadow-lg transition overflow-hidden"
    >
      <div className="p-4">
        <h3 className="font-semibold text-lg">{slugKey.replaceAll("-", " ")}</h3>
        <p className="text-sm text-muted-foreground">Read more →</p>
      </div>
    </Link>
  );
};

const API_ORIGIN = "https://modularobjects.com";
const toAbs = (url) => {
  if (!url) return "";
  if (/^https?:\/\//i.test(url)) return url;
  return `${API_ORIGIN}${url.startsWith("/") ? "" : "/"}${url}`;
};

function intersectCount(a = [], b = []) {
  const setB = new Set((b || []).map((x) => String(x).toLowerCase()));
  return (a || []).reduce((acc, x) => acc + (setB.has(String(x).toLowerCase()) ? 1 : 0), 0);
}

function getRelatedProjectsForBlog(allProjects, post, limit = 6) {
  const blogTags = post?.tags || [];
  return (allProjects || [])
    .map((p) => {
      const tagScore = intersectCount(p.tags || [], blogTags);
      return { ...p, _score: tagScore };
    })
    .sort((a, b) => (b._score !== a._score ? b._score - a._score : (b.year || 0) - (a.year || 0)))
    .slice(0, limit);
}

const BlogPost = () => {
  // ✅ 1) route params
  const { slug, lang } = useParams();

  // ✅ 2) stabilan jezik (sr/en)
  const safeLang = (lang || "en").toLowerCase().startsWith("sr") ? "sr" : "en";

  // ✅ 3) i18n
  const { t, i18n } = useTranslation();

  useEffect(() => {
    if (i18n.language !== safeLang) i18n.changeLanguage(safeLang);
  }, [safeLang, i18n]);


  const [post, setPost] = useState(null);
  const [indexItems, setIndexItems] = useState([]);
  const articleRef = useRef(null);

  const [openLeadModal, setOpenLeadModal] = useState(false);
const [leadFormKey, setLeadFormKey] = useState(0);

  const [allProjects, setAllProjects] = useState([]);
const [projectsLoading, setProjectsLoading] = useState(false);

const relatedProjects = useMemo(() => {
  if (!post) return [];

  // 1) RUČNO (preporuka)
  const slugs = Array.isArray(post.relatedProjects) ? post.relatedProjects : [];
  if (slugs.length) {
    const map = new Map(allProjects.map((p) => [p.slug, p]));
    return slugs.map((s) => map.get(s)).filter(Boolean).slice(0, 6);
  }

  // 2) FALLBACK: po tagovima
  return getRelatedProjectsForBlog(allProjects, post, 6).filter(p => (p._score || 0) > 0);
}, [post, allProjects]);

const normalizeProject = (p) => ({
  ...p,
  cover: toAbs(p.cover || p.cover_image || p.coverImage),
  tags: Array.isArray(p.tags) ? p.tags : [],
});

useEffect(() => {
  let mounted = true;
  (async () => {
    setProjectsLoading(true);
    try {
      const r = await fetch("https://modularobjects.com/marketing/api/projects");
      const j = await r.json();
      const list = Array.isArray(j) ? j : [];
      if (mounted) setAllProjects(list.map(normalizeProject));
    } catch (e) {
      console.error("❌ load projects for blog:", e);
      if (mounted) setAllProjects([]);
    } finally {
      if (mounted) setProjectsLoading(false);
    }
  })();
  return () => (mounted = false);
}, []);

  // 1) Učitaj index.json za dati jezik
  useEffect(() => {
    let mounted = true;

    import(`../../data/blog/${safeLang}/index.json`)
      .then((m) => {
        if (!mounted) return;
        const items = Array.isArray(m.default) ? m.default : (m.default?.posts || []);
        setIndexItems(items);
      })
      .catch((err) => {
        console.error("❌ Error loading blog index:", err);
        setIndexItems([]);
      });

    return () => {
      mounted = false;
    };
  }, [safeLang]);

  

  // 2) Učitaj post JSON preko "file" iz index-a
  useEffect(() => {
    let mounted = true;

    const entry = indexItems.find((x) => x.slug === slug);
    if (!entry?.file) return;

    import(`../../data/blog/${safeLang}/${entry.file}.json`)
      .then((m) => {
        if (!mounted) return;
        setPost(m.default);
      })
      .catch((err) => console.error("❌ Error loading blog content:", err));

    return () => {
      mounted = false;
    };
  }, [slug, safeLang, indexItems]);

  const bodyHtml = useMemo(() => {
    if (!post?.bodyHtml) return null;
    return post.bodyHtml.replaceAll("__LANG__", safeLang);
  }, [post, safeLang]);

  // Obeleži eksterne linkove sa ↗ indikatorom
  useEffect(() => {
    if (!articleRef.current) return;
    const anchors = articleRef.current.querySelectorAll('a[target="_blank"]');
    anchors.forEach((a) => a.classList.add("ext-link"));
  }, [bodyHtml, post]);

  if (!post) return <div className="p-4">Loading...</div>;

  const metaDesc =
    post.description ||
    (post.body && post.body.length ? String(post.body[0]).slice(0, 160) : "");

  const ogImg = post.og_image || post.image;

  return (
    <>
      <SEO
  type="article"
  lang={safeLang}
  title={post.title}
  description={metaDesc}
  image={ogImg}
  url={`/${safeLang}/blog/${slug}`}
  alternates={[
    { lang: "sr", href: `/sr/blog/${slug}` },
    { lang: "en", href: `/en/blog/${slug}` },
  ]}
/>

      <div className="p-6 max-w-3xl mx-auto">
        <header className="mb-6">
          <h1 className="text-3xl font-bold mb-2">{post.title}</h1>
          <p className="text-sm text-gray-500">
            {post.author} · {post.date}
          </p>
        </header>

        {post.image && (
          <img
            src={post.image}
            alt={post.title}
            className="w-full h-64 object-cover rounded-lg mb-8"
            loading="lazy"
          />
        )}

        {/* stil za eksterne linkove (↗) */}
        <style>
          {`
            .ext-link::after {
              content: "↗";
              margin-left: .35rem;
              font-size: .9em;
            }
          `}
        </style>

        {bodyHtml ? (
          <article
            ref={articleRef}
            className="
              prose prose-lg max-w-none dark:prose-invert
              [&_a]:text-primary-light dark:[&_a]:text-primary-dark
              [&_a]:font-medium
              hover:[&_a]:underline
            "
          >
            <div dangerouslySetInnerHTML={{ __html: bodyHtml }} />
          </article>
        ) : (
          <article
            ref={articleRef}
            className="
              prose prose-lg max-w-none dark:prose-invert
              [&_a]:text-primary-light dark:[&_a]:text-primary-dark
              [&_a]:font-medium
              hover:[&_a]:underline
            "
          >
            {post.body?.map((raw, i) => {
              const html = mdLinksToHtml(String(raw));
              return <p key={i} dangerouslySetInnerHTML={{ __html: html }} />;
            })}
          </article>
        )}

        
 <div className="mt-10">
  <ShareButtons post={{ ...post, slug }} lang={safeLang} kind="blog" />
</div>

        {/* ✅ BLOG CTA (soft) */}
<section className="mt-12">
  <div className="rounded-2xl border border-gray-200 dark:border-gray-800 bg-background-light dark:bg-background-dark p-6 sm:p-8 space-y-3">
    <h2 className="text-xl font-semibold">
      {t("cta_title")}
    </h2>

    <p className="text-text-light/70 dark:text-text-dark/70">
      {t("cta_subtitle")}
    </p>

    <div className="flex flex-wrap gap-3">
      <button
        type="button"
        onClick={() => {
          setOpenLeadModal(true);
          window.dataLayer?.push({
            event: "blog_lead_modal_open",
            blog_slug: slug,
            blog_title: post.title,
            blog_lang: safeLang,
          });
        }}
        className="bg-primary-light dark:bg-primary-dark text-white dark:text-black px-6 py-3 rounded-lg hover:opacity-90"
      >
        {t("cta_button")}
      </button>

      <Link
        to="/projects"
        className="px-6 py-3 rounded-lg border border-gray-200 dark:border-gray-800 hover:shadow-sm transition"
      >
       {t("cta_projects_button")}
      </Link>
    </div>
  </div>
</section>


        {relatedProjects?.length > 0 && (
  <section className="mt-12">
    <h2 className="text-xl font-semibold mb-4">{t("section_relevantProjects")}</h2>
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
      {relatedProjects.map((p) => (
        <Link
          key={p.slug}
          to={`/projects/${p.slug}`}
          className="group rounded-xl border overflow-hidden hover:shadow-lg transition bg-white dark:bg-black/10"
        >
          <div className="w-full aspect-[4/3] overflow-hidden">
            <img
              src={toAbs(p.cover)}
              alt={p.title}
              className="w-full h-full object-cover group-hover:scale-[1.02] transition"
              loading="lazy"
            />
          </div>
          <div className="p-4">
            <div className="font-semibold">{p.title}</div>
            <div className="text-sm text-gray-500">{p.location} • {p.year}</div>
          </div>
        </Link>
      ))}
    </div>
  </section>
)}

        {Array.isArray(post?.related) && post.related.length > 0 && (
          <section className="mt-12">
            <h2 className="text-xl font-semibold mb-4">{t("section_relatedArticles")}</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {post.related.map((relSlug) => (
                <RelatedCard key={relSlug} lang={safeLang} slugKey={relSlug} />
              ))}
            </div>
          </section>
        )}

       



      </div>

<BigModal
  open={openLeadModal}
  onClose={() => {
    setOpenLeadModal(false);
    setLeadFormKey((k) => k + 1);
    window.dataLayer?.push({
      event: "blog_lead_modal_close",
      blog_slug: slug,
      blog_title: post.title,
      blog_lang: safeLang,
    });
  }}
  title={t("cta_modal_title")}
>
  <LeadForm
    key={leadFormKey}
    mailserverApi="https://modularobjects.com/mailserver"
    allowAttachments={true}
    initialMessage={t("cta_initialMessage", { title: post.title })}
    context={{
      page: "blog_post",
      blog_slug: slug,
      blog_title: post.title,
      blog_lang: safeLang,
      blog_url: makePostUrl(safeLang, slug),
    }}
   onSuccess={({ apiResult, error }) => {
  if (error) return;
  if (!(apiResult?.ok || apiResult?.needsVerification)) return;

  track(EV.LEAD_SUBMIT_SUCCESS, {
    ...getConsent(),
    contact_id: apiResult?.contactId || apiResult?.contact_id || null,
    metadata: {
      page: "blog_post",
      surface: "lead_form",
      blog_slug: slug,
      blog_title: post.title,
      blog_lang: safeLang,
      needsVerification: !!apiResult?.needsVerification,
    },
  });

      if (apiResult?.ok || apiResult?.needsVerification) {
        window.dataLayer?.push({
          event: "blog_lead_submit_success",
          blog_slug: slug,
          blog_title: post.title,
          blog_lang: safeLang,
          needsVerification: !!apiResult?.needsVerification,
        });

        setTimeout(() => {
          setOpenLeadModal(false);
          setLeadFormKey((k) => k + 1);
        }, 1500);
      }
    }}
    conversion={() => {
      window.gtag?.("event", "generate_lead", { value: 1.0, currency: "RSD" });
    }}
  />
</BigModal>

{!openLeadModal && (
  <StickyCTA
    onClick={() => {
      setOpenLeadModal(true);
      window.dataLayer?.push({
        event: "blog.sticky_cta_click",
        blog_slug: slug,
        blog_title: post.title,
        blog_lang: safeLang,
      });
    }}
  />
)}

    </>
  );
};

export default BlogPost;
